import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import EditIcon from '@swf/core/assets/icons/edit.svg';
import type { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import { getCountryCode } from '@swf/core/utils/phoneVerification';

import PlaidLogo from '../../../../assets/plaid-logo.svg';
import { CardType } from '../../../../types/cardType';
import {
  convertAmountByRate,
  formatCardReference,
  getAsPayBankName,
  getBankDetails,
  getCreditTime,
  getPaySystemName,
  getSavedMethod,
  isZeroExponentCurrency
} from '../../../../utils/cashier';
import PaymentMethodLogo from '../../../PaymentMethodLogo/PaymentMethodLogo';
import PayeeRecord from '../DepositSummary/components/AmountSummary/components/PayeeRecord/PayeeRecord';

import {
  brand,
  brandInfo,
  container,
  info,
  item,
  main,
  result,
  summary,
  verification,
  wrapper,
  wrapperSummary
} from './PaymentMethodDetailsSummary.scss';

interface Props {
  type: CardType;
  onEditCallback?(): void;
  variant: 'summary' | 'result' | 'verification';
  depositSuccess?: boolean;
}

const hasSubSystemName = [CardType.PsPayInovapay, CardType.OpenBanking];

const PaymentMethodDetailsSummary: React.FC<Props> = ({
  type,
  onEditCallback,
  variant,
  depositSuccess
}) => {
  const showCreditTime = useFeature(FeatureFlag.SwcCreditTime);
  const country = useSelector(select.account.getCountry) as Country;

  const {
    data: {
      asPaybankInfo,
      deposit,
      paySystems = [],
      paySystemDetails: { allowedSubsystems } = {},
      savedCards = [],
      savedCardInfoId,
      depositResultContext
    },
    values: { deposit: { bankId, productId, accountNumber, phoneNumber, dialingCode } = {} }
  } = useWizardContext();

  const [savedMethod] = getSavedMethod(savedCards, savedCardInfoId);
  const i18nSystemName =
    (getPaySystemName(paySystems, type) || depositResultContext?.paySystemName) ?? '';
  const creditTimeMessage = getCreditTime(type, country, depositSuccess);

  const getItemText = () => {
    if (bankId && type === CardType.PsPayAstropay) {
      const { asPayBankName, asPayBankAccountNumber } = getAsPayBankName(asPaybankInfo, bankId);
      return `${asPayBankName} ${asPayBankAccountNumber}`;
    }

    if (type === CardType.MBWay && dialingCode && phoneNumber) {
      return formatCardReference(`${getCountryCode(dialingCode)?.dialingCode}${phoneNumber}`);
    }

    if (hasSubSystemName.indexOf(type) > -1) {
      const { bankName, savedMaskedNumber } = getBankDetails(
        productId || '',
        allowedSubsystems,
        savedMethod
      );

      return `${bankName || depositResultContext?.subSystemName} ${savedMaskedNumber}`;
    }
    if (savedCardInfoId && savedMethod.savedCard.maskedCardNum) {
      return `${i18nSystemName} ${savedMethod.savedCard.maskedCardNum}`;
    }
    return `${i18nSystemName} ${
      formatCardReference(accountNumber) || depositResultContext?.maskedCardNum || ''
    }`;
  };

  return (
    <div
      className={classNames(wrapper, {
        [wrapperSummary]: variant === 'summary'
      })}
    >
      <div
        className={classNames(container, {
          [summary]: variant === 'summary',
          [result]: variant === 'result',
          [verification]: variant === 'verification',
          [brandInfo]: type && type === CardType.OpenBanking
        })}
        onClick={() => onEditCallback?.()}
      >
        {type && (
          <PaymentMethodLogo
            cardType={type}
            i18nSystemName={i18nSystemName}
            variant={variant === 'summary' ? 'summary' : 'result'}
            productId={productId}
          />
        )}
        <div className={main}>
          <div className={item}>{getItemText()}</div>
          {variant === 'result' && (
            <div className={info}>
              {showCreditTime && creditTimeMessage && <Translation {...creditTimeMessage} />}
            </div>
          )}
        </div>
        {type && variant === 'summary' && <EditIcon />}
        {variant === 'result' && (
          <div className={main}>
            <CurrencyAmount
              value={Number(
                convertAmountByRate(deposit.amount, deposit.rate, deposit.balanceCurrency, false)
              )}
              isZeroExponentCurrency={isZeroExponentCurrency(deposit.balanceCurrency)}
              currency={deposit.balanceCurrency}
              dollarValue
              currencyDisplay="code"
            />
          </div>
        )}
      </div>
      {['summary', 'result'].includes(variant) && type === CardType.OpenBanking && (
        <>
          <div className={brand}>
            <PlaidLogo />
          </div>
          {variant === 'result' && <PayeeRecord />}
        </>
      )}
    </div>
  );
};

export default PaymentMethodDetailsSummary;
